import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import agentApiService from '../services/agentApiService';

const AgentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('📊 AgentDashboard component mounted');
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🚀 Fetching agent dashboard data...');
      
      const data = await agentApiService.getDashboard();
      console.log('📊 Dashboard data received:', data);
      
      if (data.success) {
        setDashboardData(data);
        // Removed success toast - only show errors
      } else {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('💥 Dashboard fetch error:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome Message */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back! 👋
        </h2>
        <p className="text-gray-600">Here's what's happening with your hotel bookings today.</p>
      </div>

      {/* Stats Cards Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Bookings */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Bookings</p>
              <p className="text-3xl font-bold text-gray-900">
                {dashboardData?.totalBookings || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <i className="fas fa-calendar-check text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        {/* Total Cancellation */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600 mb-1">Total Cancellation</p>
              <p className="text-3xl font-bold text-gray-900">
                {dashboardData?.totalBookingCancellations || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <i className="fas fa-times-circle text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>

        {/* Current Month Bookings */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Current Month Bookings</p>
              <p className="text-3xl font-bold text-gray-900">
                {dashboardData?.currentMonthReport?.totalBookings || 0}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <i className="fas fa-calendar-check text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        {/* Current Month Cancellation */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">Current Month Cancellation</p>
              <p className="text-3xl font-bold text-gray-900">
                {dashboardData?.currentMonthReport?.totalCancellations || 0}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <i className="fas fa-times-circle text-2xl text-red-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Amount Cards Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Booking Amount */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Total Booking Amount</p>
              <p className="text-2xl font-bold">
                {dashboardData?.totalBookedPrice || '₹0.00'}
              </p>
            </div>
            <div className="p-3 bg-blue-400 bg-opacity-30 rounded-full">
              <i className="fas fa-chart-bar text-2xl"></i>
            </div>
          </div>
        </div>

        {/* Total Refund Amount */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm mb-1">Total Refund Amount</p>
              <p className="text-2xl font-bold">
                {dashboardData?.totalCancelledPrice || '₹0.00'}
              </p>
            </div>
            <div className="p-3 bg-purple-400 bg-opacity-30 rounded-full">
              <i className="fas fa-undo text-2xl"></i>
            </div>
          </div>
        </div>

        {/* Current Month Booking Amount */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm mb-1">Current Month Booking Amount</p>
              <p className="text-2xl font-bold">
                {dashboardData?.currentMonthReport?.formattedBookingAmount || '₹0.00'}
              </p>
            </div>
            <div className="p-3 bg-green-400 bg-opacity-30 rounded-full">
              <i className="fas fa-calendar text-2xl"></i>
            </div>
          </div>
        </div>

        {/* Current Month Refund Amount */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm mb-1">Current Month Refund Amount</p>
              <p className="text-2xl font-bold">
                {dashboardData?.currentMonthReport?.formattedCancellationAmount || '₹0.00'}
              </p>
            </div>
            <div className="p-3 bg-red-400 bg-opacity-30 rounded-full">
              <i className="fas fa-times-circle text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Reports */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="text-lg font-semibold">
              📅 Today's Reports
            </h3>
            <p className="text-blue-100 text-sm">
              {dashboardData?.todaySummary?.todayDate}
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center">
                <i className="fas fa-calendar-check text-blue-600 mr-3"></i>
                <span className="text-gray-700 font-medium">Today's Total Booking</span>
              </div>
              <span className="font-bold text-gray-900 text-lg">
                {dashboardData?.todaySummary?.totalTodayBookings || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center">
                <i className="fas fa-coins text-purple-600 mr-3"></i>
                <span className="text-gray-700 font-medium">Today's Booking Amount</span>
              </div>
              <span className="font-bold text-gray-900 text-lg">
                {dashboardData?.todaySummary?.formattedTodayBookingAmount || '₹0.00'}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center">
                <i className="fas fa-times-circle text-green-600 mr-3"></i>
                <span className="text-gray-700 font-medium">Today's Cancellation</span>
              </div>
              <span className="font-bold text-gray-900 text-lg">
                {dashboardData?.todaySummary?.totalTodayCancellations || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <div className="flex items-center">
                <i className="fas fa-undo text-yellow-600 mr-3"></i>
                <span className="text-gray-700 font-medium">Today's Refund Amount</span>
              </div>
              <span className="font-bold text-gray-900 text-lg">
                {dashboardData?.todaySummary?.formattedTodayCancellationAmount || '₹0.00'}
              </span>
            </div>
          </div>
          
          {/* Available Funds */}
          <div className="p-6 bg-gradient-to-r from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold mb-2">
                  <i className="fas fa-wallet mr-2"></i>Available Funds
                </h4>
                <p className="text-3xl font-bold">
                  {dashboardData?.wallet?.formattedTotalBalance || '₹0.00'}
                </p>
              </div>
              <div className="p-3 bg-green-400 bg-opacity-30 rounded-full">
                <i className="fas fa-piggy-bank text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Progress Tracker */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <h3 className="text-lg font-semibold">📊 Monthly Progress Tracker</h3>
            <p className="text-indigo-100 text-sm">Track your monthly performance</p>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Booking</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cancellation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData?.monthlyReport?.length > 0 ? (
                    dashboardData.monthlyReport.map((report, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {report.month}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {report.totalBookings}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {report.totalCancellations}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {report.formattedBookingAmount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {report.formattedCancellationAmount}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        <i className="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
                        <p>No monthly data available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Dashboard Button */}
      <div className="mt-6 text-center">
        <button
          onClick={fetchDashboardData}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          🔄 Refresh Dashboard
        </button>
      </div>
    </div>
  );
};

export default AgentDashboard;